import React, {Dispatch, SetStateAction, useEffect, useRef, useState} from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n';
import Button from '@salesforce/design-system-react/components/button';
import CQFreshInstallationTab from 'components/cq-fresh-installation-tab';
import { IconSettings, Toast } from '@salesforce/design-system-react';
import CQTask from 'components/cq-task';
import { Statuses, TaskInput } from 'interfaces/ITask.interface';
import { CQEmptyState } from 'components/cq-empty-state';
import { CQInputs } from 'components/cq-inputs';
import { SFConnection } from 'common/sf-connector';
import { CQAppConstant } from 'app-constants';
import CQTabs from 'components/cq-tabs';
import TabPanel from 'components/cq-tabs/pane';
import Updates from 'components/cq-update';
import CQSummaryReport from 'components/cq-summary-report';
import Icon from '@salesforce/design-system-react/components/icon';
import CQCurrentOrgInfoModal from 'components/cq-current-org-info-modal';
import { IApplicableModules, IBaseTask, IUpgradeSchema } from 'interfaces/IUpgradeSchema.interface';
import { IPastRun, ITaskRun } from 'interfaces/IHistory.interface';
import { IOrgInfo, MigrationLoginStatus } from 'interfaces/IModal.interface';
import { CQRestartPollingModal } from '../cq-restart-polling-modal';
import DefaultTaskEngine from 'tasks/task-engine';
import { getCoreVersionForReleaseNotes, getLatestFinalVersion, getModuleActionButtonLabelKey, getNoteType, getProcessTypeLowercase, getReleaseNotesUrl, isFreshModule, isRefreshModule, isUpdatesModule, isUpgradesModule } from 'common/module.utils';
import CQTaskError from 'components/cq-task-error';


const TOP_TAB = 'top_tab'
interface NavigationData {
    id: string;
    label: string;
}

/**
 * Returns the currently selected org id stored in local storage.
 */
function getCurrentOrgId() {
    return localStorage.getItem(CQAppConstant.LOGGED_IN_ORG_ID) || '';
}

/**
 * Builds the migration user storage key scoped to an org id.
 */
function getMigrationUserStorageKey(orgId: string) {
    return `${CQAppConstant.MIGRATION_USER_KEY}:${orgId || 'unknown'}`;
}

/**
 * Reads the stored migration user for the provided org id.
 */
function readStoredMigrationUser(orgId: string) {
    const stored = localStorage.getItem(getMigrationUserStorageKey(orgId));
    try {
        return stored ? (JSON.parse(stored) as { username?: string; loginUrl?: string }) : null;
    } catch (error) {
        console.warn('[Migration Login] Failed to parse stored migration user', error);
        return null;
    }
}

export interface SubModulesProps {
    module: IApplicableModules;
    lastRun ?: boolean;
    lastSkipped ?: boolean;
    readonlySkippedTasks ?: boolean;
    failedModule ?: boolean;
    setViewHistoryButton?: Dispatch<SetStateAction<boolean>>;
    lastSkippedModule?: IApplicableModules;
    activeTask?: boolean;
    orgInfo: IOrgInfo;
    discardLastIncompleteRun?: boolean;
    onDiscardLastIncompleteRun?: Dispatch<SetStateAction<boolean>>;
    setCurrentModule : Dispatch<SetStateAction<string>>;
    setLastRunCompletedModule : Dispatch<SetStateAction<string>>;
    failedTask?: ITaskRun;
    notifyRunStart: (runId: string) => void;
    onSelectFreshPackages?: () => void;
}


function CQSubmodules(props: SubModulesProps) {
    const {t} = useTranslation();
    const [selectedModule, setSelectedModule] = useState<IApplicableModules>();
    const [variables, setVariables] = useState<any>({});
    const [categories, setCategories] = useState<any>([]);
    const [isLocked, setIsLocked] = useState<boolean>(false);
    const [isCompleted, setIsCompleted] = useState<boolean>(false);
    const [activeTask, setActiveTask] = useState<IBaseTask | undefined>(undefined);
    const [activeTaskProgress, setActiveTaskProgress] = useState<number | undefined>(undefined);
    const [serverErrorMsg, setServerErrorMsg] = useState<string>();
    const [selectedTasks, setSelectedTasks] = useState<any>();
    const [selectedCount, setSelectedCount] = useState<number>(0);
    const [nothingToDo, setNothingToDo] = useState<boolean>(false);
    const [rerunSkippedTask, setRerunSkippedTask] = useState<boolean>(false);
    const [hasTaskFailed, setHasTaskFailed] = useState<boolean>(false);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [selectedFormodule, setSelectedFormodule]= useState<string>("");
    const [selectedModuleIdentifier, setSelectedModuleIdentifier] = useState<string>("");
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [selectedVersionName, setSelectedVersionName] = useState<string>("");
    const [isContinueClicked, setIsContinueClicked] = useState<boolean>(false);
    const [isRequestInitiated, setIsRequestInitiated] = useState<boolean>(false);
    const [buttonTriggered, setButtonTriggered] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<string>('');
    const [currentRunMap, setCurrentRunMap] = useState<IPastRun>({moduleId: '', tasks: [], variables: {}, startedOn: null, endedOn: null});
    const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
    const intervalDurationRef = useRef(CQAppConstant.INTERVAL_THRESHOLD); // 10 seconds
    // fetchActiveTask reschedules itself via direct setInterval calls, outside
    // React's effect cycle, so these refs are how that stale closure chain sees
    // the latest failure/completion status and stops itself.
    const hasTaskFailedRef = useRef(false);
    const isCompletedRef = useRef(false);
    const prevActiveTaskRef = useRef<string | undefined>();
    const newRunSeenRef = useRef<boolean>(false);
    // Ensures the parent is notified only once per run when it starts (transition
    // from no active task -> active task), avoiding repeated re-initialization.
    const hasNotifiedRunStartRef = useRef<boolean>(false);
    const [intervalDuration, setIntervalDuration] = useState<number>(0);
    const [restartPolling, setRestartPolling] = useState<number>(0);
    const [queueStatus, setQueueStatus] = useState<string | null>(null);
    const [matchingTasks, setMatchingTasks] = useState<boolean>(false);
    const [showCompletionToast, setShowCompletionToast] = useState<boolean>(true);
    const [migrationUsername, setMigrationUsername] = useState<string>(() => readStoredMigrationUser(getCurrentOrgId())?.username || '');
    const [migrationLoginStatus, setMigrationLoginStatus] = useState<MigrationLoginStatus | undefined>(undefined);
    const [migrationLoginError, setMigrationLoginError] = useState<string>('');
    const migrationPopupRef = useRef<Window | null>(null);
    const migrationLoginHandledRef = useRef<boolean>(false);

    const isUpdates = isUpdatesModule(props.module.moduleIdentifier);
    const isRefresh = isRefreshModule(props.module.moduleIdentifier);    
    const isFresh = isFreshModule(props.module.moduleIdentifier);
    const moduleFreshInstalls: IUpgradeSchema[] | undefined = ((props.module as any)[CQAppConstant.APPLICABLE_FRESH_INSTALLS_KEY] as IUpgradeSchema[] | undefined);
    const hasMigrationTasks = !!selectedModule?.installationTasks?.some(task => 
        task.type === CQAppConstant.JSON_SCHEMA_TASK_TYPES.MIGRATION_TASK_TYPE_V2 
    );

    useEffect(() => {
        const storedUser = readStoredMigrationUser(getCurrentOrgId());
        setMigrationUsername(storedUser?.username || '');
    }, [props.orgInfo.name]);
    
    /**
     * This function populates the upgrade categories based on the provided module.
     * @param allCategories : NavigationData[] : The array to populate with categories.
     * @param module  : IApplicableModules : The module containing upgrade information.
     * @returns 
     */
    function getUpgradeCategories(allCategories: NavigationData[], module: IApplicableModules) {
        let initialLength = allCategories.length;
        switch (module.moduleIdentifier) {
            case CQAppConstant.MODULE_TYPES.UPDATES:
                allCategories.push({id: CQAppConstant.MODULE_TYPES.UPDATES, label: CQAppConstant.MODULE_TYPES.UPDATES});
                break;
            case CQAppConstant.MODULE_TYPES.REFRESH:
                allCategories.push({ id: CQAppConstant.MODULE_TYPES.REFRESH, label: CQAppConstant.MODULE_TYPES.REFRESH });
                break;
            case CQAppConstant.MODULE_TYPES.FRESH:
                allCategories.push({ id: CQAppConstant.MODULE_TYPES.FRESH, label: CQAppConstant.MODULE_TYPES.FRESH });
                break;
            default:
                if (Object.values(module).filter(value => value.applicableUpgrades).length > 0) {
                    // Exclude non-version keys (e.g. applicableFreshInstalls) that are attached
                    // to the module object for inner-tab use rather than as version tab labels.
                    Object.keys(module).filter(key => key !== CQAppConstant.APPLICABLE_FRESH_INSTALLS_KEY).forEach((key) => {
                        allCategories.push({ id: key, label: key });
                    });
                }
        }
        return allCategories.length > initialLength;
    }

    /**
     * This function generates a list of categories for navigation based on the provided module.
     * @returns 
     */
    function getCategoriesList() {
        let module = props.module;
        let allCategories : NavigationData[] = [];
        let categories = {id: TOP_TAB, label: t('submodule_toptab'), items: allCategories};

        getUpgradeCategories(allCategories, module);

        return [categories];
    }

    /**
     * Renders the `CQCurrentOrgInfoModal` component.
     *
     * This modal displays organizational information in alert mode.
     * It manages its open/close state and provides a callback for handling
     * the proceed button action.
     *
     * @returns {JSX.Element} The rendered `CQCurrentOrgInfoModal` component.
     */
    function renderOrgInfoModal() {
        const manualTask = selectedModule?.installationTasks?.find((task) => task.isManual === true);
        return (
            <CQCurrentOrgInfoModal
                alertMode={true}
                modalOpenState={isModalOpen}
                toggleModalState={() => setIsModalOpen(!isModalOpen)}
                onProceed={handleProceedButton}
                onMigrationLogin={openMigrationLoginPopup}
                showMigrationLogin={hasMigrationTasks}
                migrationLoginStatus={migrationLoginStatus}
                migrationUsername={migrationUsername}
                migrationLoginError={migrationLoginError}
                proceedDisabled={false}
                manualTask={manualTask}
                infoMessage={selectedModule?.infoMessage}
                orgInfo={props.orgInfo}
            />
        );
    };

    /**
     * Applies migration login success state and persists the migration user info.
     */
    function applyMigrationLoginSuccess(username: string, loginUrl: string) {
        if (migrationLoginHandledRef.current) return;
        migrationLoginHandledRef.current = true;
        setMigrationUsername(username);
        setMigrationLoginStatus(CQAppConstant.MIGRATION_LOGIN.STATUS.SUCCESS);
        setMigrationLoginError('');
        if (username || loginUrl) {
            const orgId = getCurrentOrgId();
            localStorage.setItem(
                getMigrationUserStorageKey(orgId),
                JSON.stringify({ username, loginUrl })
            );
        }
        setIsModalOpen(true);
    }

    /**
     * Applies migration login error state and opens the info modal.
     */
    function applyMigrationLoginError(message: string) {
        if (migrationLoginHandledRef.current) return;
        migrationLoginHandledRef.current = true;
        setMigrationLoginStatus(CQAppConstant.MIGRATION_LOGIN.STATUS.ERROR);
        setMigrationLoginError(message || 'Migration login failed.');
        setIsModalOpen(true);
    }

    /**
     * Launches the migration login popup and handles popup fallback behavior.
     */
    function openMigrationLoginPopup() {
        try {
            migrationLoginHandledRef.current = false;
            const loginUrl = props.orgInfo.isSandbox === CQAppConstant.ORG_TYPE.SANDBOX
                ? CQAppConstant.DEFAULT_URLS.SF_TEST
                : CQAppConstant.DEFAULT_URLS.SF_LOGIN;
            const pathParts = window.location.pathname.split('/').filter(Boolean); // Derive current app path segments for base path detection.
            const basePath = pathParts.length ? `/${pathParts[0]}` : ''; // Preserve app base path (e.g., /studio) for subpath deployments.
            const returnUrl = `${window.location.origin}${basePath}`;
            
            // Open popup to frontend migration-login route with params
            const params = new URLSearchParams({
                loginUrl,
                returnUrl
            });
            const screenWidth = window.screen?.availWidth || window.innerWidth;
            const screenHeight = window.screen?.availHeight || window.innerHeight;
            const popupWidth = Math.min(520, Math.floor(screenWidth * 0.9));
            const popupHeight = Math.min(720, Math.floor(screenHeight * 0.9));
            const popupLeft = Math.max(0, Math.floor((screenWidth - popupWidth) / 2));
            const popupTop = Math.max(0, Math.floor((screenHeight - popupHeight) / 2));
            const popupFeatures = `width=${popupWidth},height=${popupHeight},left=${popupLeft},top=${popupTop},menubar=no,toolbar=no,location=yes,resizable=yes,scrollbars=yes`;
            const popup = window.open(
                `/migration-login?${params.toString()}`,
                'cq-migration-login',
                popupFeatures
            );
            
            if (!popup) {
                // Fallback: if popup blocked, redirect main window
                sessionStorage.setItem('cqMigrationReturnUrl', window.location.href);
                window.location.href = `/migration-login?${params.toString()}`;
                return;
            }
            
            migrationPopupRef.current = popup;
            popup.focus();
        } catch (error: any) {
            console.error('Failed to open migration login popup:', error);
            applyMigrationLoginError(error?.message || 'Failed to open login popup');
        }
    }
    /**
     * Initializes task input variables with their default values.
     * 
     * This function is called when a module is selected to populate the initial
     * state of all task inputs with their configured default values. It ensures
     * that all variables are properly initialized, including boolean values
     * that might default to false.
     * 
     * @param taskInputs - Array of task input configurations containing default values
     * @param defaultVariables - Object to populate with the default values
     */
    function updateVariable(taskInputs: TaskInput[], defaultVariables: any) {
        
        taskInputs.forEach(input => {
            defaultVariables[input.name] = input.default;
        });
    }

    function onSelectedModule(itemId: string) {
        let tasks : IBaseTask[] = [];
        let selectedModule;
        setSelectedVersionName(handleCategoryLabelSelection(itemId));

        switch (itemId) {
            case props.module.moduleIdentifier:
                selectedModule = props.module;
                tasks = props.module.installationTasks || [];
                break;
            default:
                if(itemId.indexOf(CQAppConstant.MODULE_TYPES.UPGRADE) === 0) {
                    let upgradeModule = Object.entries(props.module).find(([key, value]) => key === itemId); 
                    selectedModule = upgradeModule &&  upgradeModule[1];
                    tasks = (selectedModule as IApplicableModules).installationTasks || [];
                }
                break;
        }
        setSelectedModule(selectedModule);
        let defaultVariables : any = {};
        updateVariable(selectedModule?.inputs, defaultVariables);
        setVariables(defaultVariables);

        let selected: any = {};

        if(props.readonlySkippedTasks) {
            DefaultTaskEngine.getHistorySync()[0].tasks.forEach((task) => {
                if ((task.isSkipped && task.wasPreviouslyCompleted !== true) || task.status !== Statuses.Completed) {
                    selected[task.uniqueName || ""] = true;
                }
            })
        } else {
            tasks.forEach((task) => {
                if (task.defaultChecked !== false) {
                    selected[task.uniqueName || ""] = true;
                }
            });
        }
        setSelectedCount(Object.keys(selected).length);
        setSelectedTasks(selected);

    }

    /**
    * Extracts the version name from a category label.
    * Assumes the label starts with "Upgrade to" followed by the version name.
    * 
    * @param label - The category label string (e.g., "Upgrade to CQ Summer '24")
    * @returns The extracted version name (e.g., CQ Summer 24)
    */ 
    function handleCategoryLabelSelection(label: string) {
        const versionName = label.replace(/^Upgrade to\s+/, '').replace(/'/g, '');
        return versionName;
    }

    /**
     * Resolves the release notes URL for the currently selected module tab.
     *
     * Updates: uses `getCoreVersionForReleaseNotes`, which reads the org's currently installed
     * Core version, so the URL is tied only to Core's version, never an extension's.
     *
     * Upgrades: can't use `selectedModule.applicableUpgrades` (that's the chained tabs after
     * this one, not this tab's version). Instead uses `selectedModule.installationTasks`
     * (`forModule` == `packageName`, `finalVersion` per task), preferring Core via
     * `getLatestFinalVersion`, falling back to extensions if Core isn't present.
     *
     * @returns {string} The release notes URL to link to.
     */
    function resolveReleaseNotesUrl(): string {
        if (isUpdates) {
            return getReleaseNotesUrl(getCoreVersionForReleaseNotes());
        }
        const schemas = (selectedModule?.installationTasks || []).map((task: IBaseTask) => ({
            packageName: task.forModule,
            finalVersion: task.finalVersion,
        }));
        return getReleaseNotesUrl(getLatestFinalVersion(schemas));
    }

    /**
     * Generates a JSX element containing a link to the release notes.
     *
     * @returns {JSX.Element} A div element with a link to the release notes.
    */
    function getReleaseNotesLink() {
        const releaseNotesUrl = resolveReleaseNotesUrl();
        return (
            <span>
                {t("click_to_view")} <a href={releaseNotesUrl} target="_blank" rel="noopener noreferrer">{t("release_notes")}</a>.
            </span>
        )
    }

    /**
     * Handles the button click event to execute a series of tasks.
     * 
     * This function performs the following steps:
     * 1. Resets error state and locks the UI.
     * 2. Clears the DefaultTaskEngine.
     * 3. Iterates through the tasks and determines their status based on previous runs and user selections.
     * 4. Enqueues or skips tasks in the DefaultTaskEngine based on their status and user selections.
     * 5. Runs the DefaultTaskEngine with the selected module and variables.
     * 6. Updates the task progress and active task state during execution.
     * 7. Handles any errors that occur during execution.
     * 8. Resets the UI state and sends a notification upon completion.
     * 
     * @async
     * @function handleProceedButton
     * @returns {Promise<void>} A promise that resolves when the task execution is complete.
     */
    async function handleProceedButton(isManualTaskCompleted = false) {
        try {
            isManualTaskCompleted = typeof isManualTaskCompleted === 'boolean' && isManualTaskCompleted ? true : false;
            setIsRequestInitiated(true);
            setButtonTriggered(true);
            // A new run should always start polling at 10s, not inherit the
            // previous run's backed-off interval. Done explicitly here because
            // nothing else is guaranteed to reset/reschedule it at this point.
            intervalDurationRef.current = CQAppConstant.INTERVAL_THRESHOLD;
            setIntervalDuration(CQAppConstant.INTERVAL_THRESHOLD);
            clearActiveInterval();
            intervalIdRef.current = setInterval(fetchActiveTask, CQAppConstant.INTERVAL_THRESHOLD);
            setHasTaskFailed(false);
            setServerErrorMsg('');
            setIsLocked(true);
            setIsCompleted(false);
            setShowCompletionToast(true);
            // Reset currentRunMap when rerunning to clear stale completed task statuses
            if (props.lastRun || props.failedModule || hasTaskFailed) {
                setCurrentRunMap({moduleId: '', tasks: [], variables: {}, startedOn: null, endedOn: null});
                newRunSeenRef.current = false;
                hasNotifiedRunStartRef.current = false;
            }
            if(selectedModule?.installationTasks){
                let hasAnyTaskToRun = false;
                for(let index = 0 ; index < selectedModule?.installationTasks.length ; index++){
                    let task = selectedModule.installationTasks[index];
                    selectedModule.installationTasks[index].isSelected = selectedTasks[task.uniqueName]
                    if (selectedTasks[task.uniqueName]) hasAnyTaskToRun = true;
                }
                
                // when re running skipped tasks if none is selected then just set completed
                if (props.readonlySkippedTasks && !hasTaskFailed && !hasAnyTaskToRun) {
                    setIsCompleted(true);
                    setButtonTriggered(false);
                    setIsLocked(false);
                    setIsRequestInitiated(false);
                    if (props.setViewHistoryButton) props.setViewHistoryButton(true);
                    props.setLastRunCompletedModule(selectedModule?.moduleIdentifier || '');
                    return;
                }
            }

            let payload: any = {
                selectedModule,
                variables,
                isManualTaskCompleted,
                lastRun: props.lastRun || isManualTaskCompleted,
                readonlySkippedTasks: props.readonlySkippedTasks && !hasTaskFailed,
                lastFailed: props.failedModule || hasTaskFailed,
                discardLastIncompleteRun: props.discardLastIncompleteRun
            };
            if (hasMigrationTasks) {
                const migrationUser = readStoredMigrationUser(getCurrentOrgId());
                payload = {
                    ...payload,
                    migrationUser: migrationUser || (migrationUsername ? { username: migrationUsername, loginUrl: '' } : null)
                };
            }
            await SFConnection.getAxiosInstance().post(CQAppConstant.EXECUTE_TASK, payload);

        } catch (ex) {
            if((ex as any).response?.status !== 304) {
                setIsRequestInitiated(false); 
                setServerErrorMsg((ex as any).response?.data?.error || (ex as any).message + '\n' + (ex as any).stack);
                clearActiveInterval();
            }
        }
    };

    /**
     * Handles the discard button click event to discard the last incomplete run.
     * 
     * This function performs the following steps:
     * 1. Calls the parent component to handle the discard action
     * 2. Resets the error state
     * 3. Clears the last incomplete run data
     * 
     * @function handleDiscardButton
     * @returns {void}
     */
    function handleDiscardButton() {
        try {
            // Reset local state
            setHasTaskFailed(false);
            setIsCompleted(false);
            setButtonTriggered(false);
            setActiveTask(undefined);
            hasNotifiedRunStartRef.current = false;

            // Call parent to handle discard (show all tabs and remove lastIncompleteRun)
            props.onDiscardLastIncompleteRun?.(true);

            // On clicking the discard button in lastRun or current running panel, the user is redirected to the default Updates tab and render the UI accordingly.
            if(props.lastRun || props.activeTask) {
                DefaultTaskEngine.currentModule = CQAppConstant.MODULE_TYPES.UPDATES;
                props.setCurrentModule?.(CQAppConstant.MODULE_TYPES.UPDATES);
            }

            // Trigger listeners for backup (skip API call since we already have the data)
            DefaultTaskEngine.invokeListeners(true);
        } catch (ex) {
            setServerErrorMsg((ex as any).message + '\n' + (ex as any).stack);
        }
    };

    useEffect(() => {
        let cats = getCategoriesList();
        setCategories(cats);
        const hasFreshInstalls = Array.isArray(moduleFreshInstalls) && moduleFreshInstalls.length > 0;
        if((cats.length === 0 || cats[0].items.length === 0) && !hasFreshInstalls) {
            setNothingToDo(true);
        } else {
            setNothingToDo(false);
            // Only select a first module if there are version tabs available.
            // If there are only fresh installs (no upgrade version tabs), skip this.
            if (cats.length > 0 && cats[0].items.length > 0) {
                let firstModule = cats[0].items[0].id;
                console.log('Defaulting to ' + firstModule);
                if(!selectedModule){
                    onSelectedModule(firstModule);
                }
            }
        }
        if(props.failedModule){
            setHasTaskFailed(true);
            props.setViewHistoryButton?.(false);
        }

    }, [props.module]);

    // Migration login flow:
    // - Listen for postMessage events from the login popup and apply success/error state.
    // - Support fallback redirects that return status via query params.
    // - Remove migration login params from the URL after handling.
    useEffect(() => {
        migrationLoginHandledRef.current = false;
        const handleMessage = (event: MessageEvent) => {
            if (!migrationPopupRef.current || event.source !== migrationPopupRef.current) return;
            const data = event.data as {
                migrationLogin?: MigrationLoginStatus;
                migrationUsername?: string;
                migrationLoginUrl?: string;
                message?: string;
            };
            if (!data?.migrationLogin) return;
            if (data.migrationLogin === CQAppConstant.MIGRATION_LOGIN.STATUS.SUCCESS) {
                applyMigrationLoginSuccess(data.migrationUsername || '', data.migrationLoginUrl || '');
            } else {
                applyMigrationLoginError(data.message || 'Migration login failed.');
            }
            migrationPopupRef.current?.close();
        };

        window.addEventListener('message', handleMessage);
        const params = new URLSearchParams(window.location.search);
        const migrationLogin = params.get(CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.STATUS);

        if (migrationLogin === CQAppConstant.MIGRATION_LOGIN.STATUS.SUCCESS) {
            const username = params.get(CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.USERNAME) || '';
            const loginUrl = params.get(CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.LOGIN_URL) || '';
            applyMigrationLoginSuccess(username, loginUrl);
        } else if (migrationLogin === CQAppConstant.MIGRATION_LOGIN.STATUS.ERROR) {
            applyMigrationLoginError(params.get(CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.MESSAGE) || 'Migration login failed.');
        }

        if (migrationLogin) {
            params.delete(CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.STATUS);
            params.delete(CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.USERNAME);
            params.delete(CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.MESSAGE);
            params.delete(CQAppConstant.MIGRATION_LOGIN.QUERY_KEYS.LOGIN_URL);
            const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
            window.history.replaceState({}, document.title, newUrl);
        }

        return () => window.removeEventListener('message', handleMessage);
    }, []);

    /**
     * useEffect — Controls polling behavior for fetching the active task.
     *
     * - Polls only when:
     *      • buttonTriggered = true   OR
     *      • props.activeTask exists
     * - Stops polling when:
     *      • isCompleted = true
     *      • hasTaskFailed = true
     * - Clears intervals on cleanup to prevent memory leaks.
     *
     * Dependencies:
     *  @param {boolean} buttonTriggered - Whether polling was manually started.
     *  @param {boolean} isCompleted - Whether the task run is finished.
     *  @param {boolean} hasTaskFailed - Whether a task failure occurred.
     *  @param {number} intervalDuration - Current polling interval duration.
    */
    useEffect(() => {
        hasTaskFailedRef.current = hasTaskFailed;
        isCompletedRef.current = isCompleted;
        const shouldPoll = (props.activeTask !== undefined || props.failedModule || buttonTriggered) && !isCompleted && !hasTaskFailed;
        if(props.activeTask !== undefined ){
            setIsLocked(true);
        } 
        checkIsCompletedOrFailed();
        
        if(!shouldPoll) return;

        if (shouldPoll) {
            fetchActiveTask();
            clearActiveInterval();
            intervalIdRef.current = setInterval(fetchActiveTask, intervalDurationRef.current);
        }
        return () => { clearActiveInterval();};
    }, [buttonTriggered, isCompleted, hasTaskFailed, restartPolling]);


    /**
     * Clears the active interval if it exists.
     * Useful for stopping repeated actions (e.g., timers) when a component unmounts
     * or when you need to reset the interval.
     */
    function clearActiveInterval(){
         if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    }

    /**
     * This function checks if the current task run is completed or has failed and clears the polling interval if so.
     * @returns 
     */
    function checkIsCompletedOrFailed(){
         if (isCompleted || hasTaskFailed) {
            clearActiveInterval();
            setActiveTask(undefined);
            setIsLocked(false);
            return;
        }
    }
    /**
     * Resets polling speed to 10s when the active task genuinely changes.
     * Kept in its own effect (deps: [activeTask] only) so currentRunMap/
     * queueStatus churn on every poll can't spuriously trigger this reset.
     */
    useEffect(() => {
        console.log('Active Task changed: Gone to new value', activeTask?.inputs.taskName);
        intervalDurationRef.current = CQAppConstant.INTERVAL_THRESHOLD;  // 10 seconds
        setIntervalDuration(CQAppConstant.INTERVAL_THRESHOLD);
    }, [activeTask]);

    /**
     * useEffect — Runs whenever a new activeTask is fetched.
     *
     * Responsibilities:
     *  - Stops polling if the run is complete or errored.
     *  - Detects:
     *      • All tasks completed → setIsCompleted(true)
     *      • Any task failed → setHasTaskFailed(true)
     *  - Updates UI state such as locking and clearing activeTask.
     *
     * Note: polling speed reset lives in the effect above, not here.
     *
     * Dependencies:
     *  @param {IBaseTask | undefined} activeTask - The currently active task.
    */
    useEffect(() => {
        const tasks = currentRunMap.tasks;
        const hasMatchingTasks = (selectedModule?.installationTasks || []).find(currentTasks => currentTasks.uniqueName === tasks[0]?.uniqueName);
        setMatchingTasks(!!hasMatchingTasks);

        // Sync selection to the actual run data, since a remount has no memory of what was deselected.
        if (props.activeTask && hasMatchingTasks && tasks.length > 0) {
            const actualSelection: Record<string, boolean> = {};
            const tasksByUniqueName = new Map(tasks.map(t => [t.uniqueName, t]));
            (selectedModule?.installationTasks || []).forEach((task) => {
                if (!task.uniqueName) return;
                const runTask = tasksByUniqueName.get(task.uniqueName);
                actualSelection[task.uniqueName] = runTask ? !runTask.isSkipped : false;
            });
            setSelectedTasks(actualSelection);
        }

        // Check completion
        const isAllTaskCompleted = tasks.length > 0 && tasks.every(t => t.status === Statuses.Completed) && queueStatus === CQAppConstant.INSTALLATION_STATUSES.COMPLETED && !!hasMatchingTasks;
        // Check failure
        const failedTask = tasks.find(t => t.status === Statuses.Failed) && queueStatus === CQAppConstant.INSTALLATION_STATUSES.FAILED;
        
        // Nothing to do if neither completed nor failed
        if (!isAllTaskCompleted && !failedTask) return;

        
        if (isAllTaskCompleted) {
            DefaultTaskEngine.invokeListeners();
            setActiveTask(undefined);
            if (props.setViewHistoryButton) {
                props.setViewHistoryButton(true);
            }
            setIsCompleted(true);
            setButtonTriggered(false);
            clearActiveInterval();
            hasNotifiedRunStartRef.current = false;
            props.setLastRunCompletedModule(currentRunMap.moduleId);
        }
        
        if (failedTask && !isRequestInitiated) {
            DefaultTaskEngine.invokeListeners();
            setActiveTask(undefined);
            setHasTaskFailed(true);
            props.setViewHistoryButton?.(false);
            setIsContinueClicked(true);
            setButtonTriggered(false);
            clearActiveInterval();
            hasNotifiedRunStartRef.current = false;
        }       
    }, [activeTask, queueStatus, currentRunMap, isRequestInitiated]);

    /**
     * useEffect — Cleans up the continuing state when task is successfully submitted
     * 
     * Resets the isRequestInitiated flag when:
     * - Queue status becomes SUBMITTED after continue operation
     * - No error is present
     */
    useEffect(() => {
        if (queueStatus && Object.values(CQAppConstant.ACTIVE_STATUSES).includes(queueStatus) && isRequestInitiated) {
            setIsRequestInitiated(false);
        }
    }, [queueStatus, isRequestInitiated]);

    /**
     * Normalizes a raw Salesforce task object into an IBaseTask.
     *
     * Adds/overwrites fields in task.inputs:
     *  - taskName → task.name
     *  - description → task.description
     *
     * @param {any} task - Raw task object returned from Salesforce.
     * @returns {IBaseTask} - Normalized task conforming to the application interface.
    */
    const normalizeToIBaseTask = (task: any): IBaseTask => ({
        ...task,
        inputs: {
            ...task.inputs,
            taskName: task.name,
            description: task.description,
            subTasks:  task.subTasks,
            preMigrationTasks: task.preMigrationTasks,
            postMigrationTasks: task.postMigrationTasks
        },
        taskStatus: task.status
    });

    /**
     * Fetches and processes the currently active task from Salesforce.
     *
     * Process:
     *  1. Queries the CQInstallationLog document.
     *  2. Fetches document content and parses JSON.
     *  3. Updates:
     *      • currentRunMap  → Parsed tasks array.
     *      • activeTask     → Currently active task.
     *  4. Handles polling speed increase when the same task persists.
     *  5. Stops polling when maximum interval is reached (10 minutes).
     *
     * @async
     * @returns {Promise<void>}
     *
     * Salesforce Queries:
     *  - Reads the latest 'CQInstallationLog' document in the 'Compliance_Quest' folder.
     *
     * Effects:
     *  - setCurrentRunMap()
     *  - setActiveTask()
     *  - setActiveTab()
     *  - setIntervalDuration()
    */
    async function fetchActiveTask() {
        // Stop here if the task failed/completed while this request was in flight,
        // otherwise it would reschedule another poll after polling already stopped.
        if (hasTaskFailedRef.current || isCompletedRef.current) {
            clearActiveInterval();
            return;
        }
        console.log('Fetching active task from Salesforce...', intervalDurationRef.current);
        let status = await SFConnection.getAxiosInstance().get(CQAppConstant.STATUS);
        let currentState = status.data;
        
        if(!currentState.activeTask && !currentState[CQAppConstant.CQ_DOCUMENT_LOG.CQ_TASKS]) {
            // No prior run for this module (nothing deployed yet) - increase the
            // polling interval anyway, or it stays pinned at 10s.
            setActiveTask(undefined);
            increasePollingInterval(undefined);
            return;
        }
        
        setQueueStatus(currentState.status);

        const tasksRaw = currentState[CQAppConstant.CQ_DOCUMENT_LOG.CQ_TASKS];
        const activeTaskRaw = currentState[CQAppConstant.CQ_DOCUMENT_LOG.ACTIVE_TASK];

        // Stable identifier for the current run (its start timestamp), used to
        // signal the parent once per run start across remounts.
        let currentRunId: string | undefined;

        if (tasksRaw) {
            const allTasks = JSON.parse(tasksRaw)[0];
            currentRunId = allTasks?.startedOn ? String(allTasks.startedOn) : undefined;
            const isResumingRun = isContinueClicked || props.lastRun;
            const hasNewRunStarted = allTasks.tasks?.some((t: any) => t.status === Statuses.Pending);
            if (hasNewRunStarted) newRunSeenRef.current = true;
            // Update currentRunMap when new run has started, or allow all updates including completion (after new run is seen)
            if (!isResumingRun || (allTasks.continueFromPreviousState && !(props.readonlySkippedTasks && !newRunSeenRef.current))) {
                setCurrentRunMap(allTasks);
            }
        }

        if (activeTaskRaw) {
            if (activeTaskRaw !== prevActiveTaskRef.current) {
                prevActiveTaskRef.current = activeTaskRaw;
                const fetchedActiveTask: IBaseTask = JSON.parse(activeTaskRaw);
                //normalizeToIBaseTask is used to map the fetched task to IBaseTask interface
                setActiveTask(normalizeToIBaseTask(fetchedActiveTask));
                setActiveTab(fetchedActiveTask?.moduleId || '');

                // Signal the parent once per run start, and only when not already on
                // the "Currently Running Module" tab. The parent dedups by run id so a
                // remount cannot re-trigger a reload for the same still-active run.
                if (!hasNotifiedRunStartRef.current && !props.activeTask) {
                    hasNotifiedRunStartRef.current = true;
                    props.notifyRunStart(currentRunId ?? activeTaskRaw);
                }
                // Manual tasks that are actively pending require user interaction.
                // Stop polling — it restarts automatically when the user clicks
                // Proceed (which sets buttonTriggered=true via handleProceedButton).
                if (fetchedActiveTask.isManual && fetchedActiveTask.status === Statuses.Pending) {
                    clearActiveInterval();
                    return;
                }
            } else {
                increasePollingInterval(activeTaskRaw);
            }
        } else {
            setActiveTask(undefined);
            increasePollingInterval(undefined);
        }
    }

    /**
     * Increases the polling interval by a defined threshold until it reaches the
     * maximum limit, then stops scheduling further polls.
     */
    function increasePollingInterval(activeTaskRaw: string | undefined){
        // Stops this instance's chain once the user leaves this module's tab.
        const isThisModuleActive = DefaultTaskEngine.currentModule === props.module.moduleIdentifier;
        // Active task, button just clicked, or this tab is open (and not failed/completed).

        const shouldContinuePolling =
            (activeTaskRaw !== undefined || buttonTriggered || isThisModuleActive)
            && !hasTaskFailedRef.current
            && !isCompletedRef.current;
        
        if (!shouldContinuePolling) {
            clearActiveInterval();
            return;
        }
        const currentInterval = intervalDurationRef.current;
        const newInterval = Math.min( currentInterval + CQAppConstant.INTERVAL_THRESHOLD, CQAppConstant.POLLING_MAX_INTERVAL); // Max 10 minutes

        intervalDurationRef.current = newInterval;
        setIntervalDuration(newInterval);
        clearActiveInterval();

        // Stop scheduling once the max interval is reached instead of polling further.
        if (newInterval !== CQAppConstant.POLLING_MAX_INTERVAL) {
            intervalIdRef.current = setInterval(fetchActiveTask, newInterval);
        }
    }

    function renderTaskError() {
        const failedTask = currentRunMap.tasks.find(task => task.status === Statuses.Pending || task.status === Statuses.Failed) || props.failedTask;

        return (
            <CQTaskError
                failedTask={failedTask}
                onClose={() => {
                    setHasTaskFailed(false);
                    setIsCompleted(false);
                }}
            />
        );
    };

    /**
     * Renders a success message using the Salesforce Lightning Design System (SLDS) Toast component.
     * The message indicates that a patch operation for the selected module was completed successfully.
     *
     * @returns {JSX.Element} A JSX element containing the success message.
     */
    function renderSuccessMessage () {
        const upgradeType = getProcessTypeLowercase(props.module.moduleIdentifier);
        return(
            <IconSettings iconPath="/assets/icons">
                <div role="alert">
                    <Toast
                        className="slds-size_1-of-1 slds-border_radius_none"
                        labels={{ heading: t("task_success_message", { selectedFormodule, processType: upgradeType }) }}
                        onRequestClose={() => setIsSuccess(false)}
                        variant="success"
                        duration={5000}
                    />
                </div>    
            </IconSettings>
        );
    }

    /**
     * Renders a queued installation notification when the current module is in a queued state.
     * The message is shown only when there is no active task and the queue status is either CREATED or SUBMITTED. 
     *
     * @returns {JSX.Element | false} A JSX fragment containing the queued notification, or false when not applicable.
     */
    function renderQueueMessage() {
        const isManual = selectedModule?.installationTasks?.find((task) => task.isManual === true);
        const { CREATED, SUBMITTED } = CQAppConstant.INSTALLATION_STATUSES;
        const isQueuedStatus = queueStatus === CREATED || queueStatus === SUBMITTED || buttonTriggered;
        const manualTaskInfo = isManual ? t('has_manual_task') : t('has_no_manual_task');
        return (!(serverErrorMsg || hasTaskFailed)  && isQueuedStatus &&
            <>
                <div className="slds-var-m-bottom_small">
                    <div className="slds-notify slds-notify_alert slds-alert_warning" role="alert">
                        <span className="slds-assistive-text">warning</span>
                        <span className="slds-icon_container slds-icon-utility-warning slds-m-right_x-small" title={manualTaskInfo}></span>
                        <h2 className="slds-text-align_left">{manualTaskInfo}</h2>
                    </div>
                </div>
            </>
        );
    }

    /**
     * Renders a "Discard" button when the failed condition is true and the module is of REFRESH type.
     * @param failedCondition - Whether the condition to show the discard button has been met
     * @returns The Discard button element or false if conditions are not met
     */
    function showDiscardButton(failedCondition: boolean) {
        return (failedCondition && props.module.moduleIdentifier === CQAppConstant.MODULE_TYPES.REFRESH && (
            <Button
                className="slds-m-right_small"
                label="Discard"
                variant="neutral"
                disabled={isLocked}
                onClick={handleDiscardButton}
            />
        ));
    }

    /**
     * Renders the primary action button for the module.
     * When `failedCondition` is true, it shows a "Continue" action that proceeds the workflow;
     * otherwise it shows the module-specific action label and opens/closes the confirmation modal.
     * @param failedCondition - Whether the module is in a failed state requiring a continue action
     * @returns The dynamic action button element
     */
    function showDynamicButton(failedCondition: boolean) {
        const buttonLabel = failedCondition ? t('task_continue') : t(getModuleActionButtonLabelKey(props.module.moduleIdentifier));
        return (
            <Button
                className='slds-text-align_left'
                label={buttonLabel}
                variant="brand"
                disabled={selectedModule?.installationTasks?.length === 0 || isLocked}
                onClick={failedCondition ? handleProceedButton : () => setIsModalOpen(!isModalOpen)}
            />
        );
    }

    /**
     * Renders the information header and action button for the module section.
     *
     * Displays a heading based on whether the current module is the updates tab,
     * and shows a button to proceed with installation or continue a previous task.
     *
     * @returns {JSX.Element} The rendered info section containing a heading and action button.
     */
    function renderInfo() {
        let failedCondition = props.lastRun === true || hasTaskFailed;
        const buttonInfoText = t(getModuleActionButtonLabelKey(props.module.moduleIdentifier));
        return (
            <div className="slds-grid slds-wrap slds-p-bottom_medium slds-grid_align-spread slds-text-align_left">
                <div className="slds-text-heading_medium slds-col slds-size_9-of-12">
                    {isUpdates && t("update_release")}
                    {!isRefresh && (
                        <div className={isUpdates ? 'slds-m-top_small' : undefined}>
                            <span className="slds-text-heading_small">
                                <Icon
                                    assistiveText={{ label: 'info_alt' }}
                                    category="utility"
                                    name="info_alt"
                                    size="x-small"
                                /> {failedCondition ? t("button_info_continue") : t("button_info", { buttonInfoText })}
                            </span>
                        </div>
                    )}
                </div>
                <div className="slds-col slds-text-align_right">
                    {showDiscardButton(failedCondition)}
                    {showDynamicButton(failedCondition)}
                </div>
            </div>
        );
    }

    /**
     * Renders the Updates, Refresh, or Fresh module if applicable data are available and the module identifier is "Updates", "Refresh", or "Fresh".
     *
     * @returns {JSX.Element | null} A JSX element containing the Updates, Refresh, or Fresh component if conditions are met, otherwise `null`.
     */
    function renderModule() {
        if (!isUpdates && !isRefresh && !isFresh) {
            return null
        }

        const applicableData: IUpgradeSchema[] = isUpdates ? (props.module.applicablePatches || []) : isRefresh ? (props.module.applicableRefreshes || []) : (moduleFreshInstalls || []);

        if ((!isCompleted || hasTaskFailed) && applicableData?.length) {
            return (
                <div>
                    {hasTaskFailed && !serverErrorMsg && renderTaskError()}
                    {!hasTaskFailed && isSuccess && renderSuccessMessage()}  
                    {!isLocked && !serverErrorMsg && (
                        isUpdates ? (
                            <div className="slds-box slds-box_xx-small slds-theme_shade slds-m-bottom_small">
                                <CQInputs
                                    taskInputs={props.module.inputs}
                                    variables={variables}
                                    onVariableChange={(attribute, newValue) =>
                                        setVariables((prev: any) => ({ ...prev, [attribute]: newValue }))
                                    }
                                />
                                {renderInfo()}
                            </div>
                        ) : (isRefresh || isFresh) ? renderInfo() : null
                    )}
                    <Updates 
                        activeTask={activeTask} 
                        setSelectedTasks={setSelectedTasks} 
                        selectedTasks={selectedTasks} 
                        matchingTasks={matchingTasks}
                        module={applicableData}
                        setIsSuccess={setIsSuccess}
                        setSelectedFormodule={setSelectedFormodule}
                        setSelectedModuleIdentifier={setSelectedModuleIdentifier}
                        currentRunMap={currentRunMap} 
                        onProceed={handleProceedButton}
                    />
                </div>
            );
        }
        if (applicableData.length === 0) {
            return <CQEmptyState isPatchCompleted={true} />
        }
    }

    /**
     * Renders information about a module, including its upgrade type and a note.
     * @returns A JSX element containing the module information, including a heading
     *          with the upgrade type and a note with the note type.
     */
    function modulesInfo() {
        const currentModule = DefaultTaskEngine.currentModule;
        const noteType = getNoteType(currentModule);
        return (
            <div className="slds-text-align_left">
                <div className="slds-text-heading_medium ">
                    <strong>{isRefresh ? t("refresh_available") : t("updates_available", { upgradeType: currentModule })}</strong>
                </div>
                <div className="slds-text-heading_small slds-p-top_x-small slds-p-bottom_medium">
                    <strong>{t("migration_login_note_label")}</strong> {isRefresh ? t("refresh_note") : t("updates_note", { noteType })} {(isUpgradesModule(currentModule) || isUpdates) && getReleaseNotesLink()}
                </div>
            </div>
        );
    }

    /**
     * This function monitors the polling interval duration and displays a modal
     * to the user if the interval exceeds a certain threshold (10 minutes).
     * @returns 
     */
    function watchPollingModal(){
        if(intervalDuration >= CQAppConstant.POLLING_MAX_INTERVAL ){
            return <CQRestartPollingModal intervalDurationRef={intervalDurationRef} setIntervalDuration={setIntervalDuration} setRestartPolling={setRestartPolling}/>
        }
        return <></>;
    }

    /**
     * Determines whether a session has expired based on the error message.
     * @param serverErrorMsg - The error message to check for session expiration.
     * @returns `true` if the error message contains 'Session expired', otherwise `false`.
     */
    function isSessionInvalid(serverErrorMsg: any): boolean {
        return serverErrorMsg ? serverErrorMsg.includes('Session expired') : false;
    }

    /**
     * Renders the information header and action button for the module section.
     *
     * Displays an error message with special handling for session invalid errors.
     * 
     * For session invalid errors, displays the message in a centered grid layout with error styling
     * and provides a "Reload" button to refresh the page. For other errors, displays the message as plain text.
     * 
     * @returns {JSX.Element} A JSX element containing the formatted error message and optional reload button.
     */
    function showErrorMessage() {
        let isSessionInvalidError = isSessionInvalid(serverErrorMsg);
        return (<div className={isSessionInvalidError ? "slds-grid slds-grid--align-center" : ""}>
            {isSessionInvalidError ? <strong className='slds-p-around--medium slds-text-color_error'>{serverErrorMsg}</strong> : serverErrorMsg}
            {isSessionInvalidError &&
                <div className="slds-p-top_x-small">
                    <span>
                        Click{' '}
                    </span>
                    <Button
                        label="Reload"
                        onClick={() => {
                            window.location.reload();
                        }}
                        variant="base"
                    />{' '}
                    <span>
                        to continue.
                    </span>
                </div>}
        </div>);
    }

    /**
     * Renders a component or message based on the completion status and module identifier.
     *
     * @returns A JSX element representing the summary report or a success message if the task is completed,
     *          or `null` if the conditions are not met.
    */
    function showCompleteReport() {
        if (!isCompleted || hasTaskFailed) return null;
        const headingKey = isRefresh ? "task_refresh_completed_message" : "task_all_completed_message";
        return (
            <>
                <IconSettings iconPath="/assets/icons">
                    {showCompletionToast && (
                        <div role="alert">
                            <Toast
                                className="slds-size_1-of-1 slds-border_radius_none"
                                labels={{ heading: t(headingKey) }}
                                onRequestClose={() => setShowCompletionToast(false)}
                                variant="success"
                            />
                        </div>
                    )}
                    {!isRefresh && (
                        <CQSummaryReport currentModule={DefaultTaskEngine.currentModule} selectedVersion={handleCategoryLabelSelection(selectedVersionName)} />
                    )}
                </IconSettings>
            </>
        );
    }

    /**
     * Extracts the module identifier from the last skipped module.
     * 
     * Handles two data structures:
     * 1. Direct structure (Updates/Refresh): forModule at root level
     * 2. Nested structure (Upgrades): forModule inside version-keyed objects
     * 
     * Note: Currently used to determine if the "rerun skipped tasks" CTA should appear
     * in the Upgrades tab only (when last skipped run was Upgrades).
     * 
     * @returns {string | undefined} The module identifier (e.g., 'Upgrades', 'Updates', 'Refresh') 
     *                                or undefined if not found
     */
    function getLastSkippedForModule() {
        // Direct forModule at root level (Updates/Refresh)
        if (props.lastSkippedModule?.forModule) {
            return props.lastSkippedModule.forModule;
        }
        
        // Nested structure (Upgrades with version keys) - search all values
        const values = Object.values(props.lastSkippedModule || {});
        const moduleObject = values.find((val: any) => 
            val && typeof val === 'object' && val.forModule
        );
        
        return moduleObject?.forModule;
    }
    
    const lastSkippedForModule = getLastSkippedForModule();
    
    const isSkipped =
        !isUpdates && !isRefresh &&  //Check if not Refresh and not Updates as Upgrades case doesnot have a moduleIdentifier at root level
        lastSkippedForModule === CQAppConstant.MODULE_TYPES.UPGRADES &&
        props.lastSkippedModule != null &&
        Object.keys(props.lastSkippedModule).length > 0;
    let activeTaskName = activeTask?.inputs.taskName;
    let shouldOpenActiveTaskPopUp = activeTask && activeTab === selectedModule?.moduleIdentifier;
    // The Fresh Installation tab is always appended last; upgrade tabs are those preceding it.
    const upgradeTabs = categories[0]?.items?.filter((cat: any) => (!isUpdatesModule(cat.label) && !isRefreshModule(cat.label) && !isFreshModule(cat.label))) ?? [];
    return <>

        {
            serverErrorMsg ?
                <>
                    <div className="slds-var-m-bottom_small">
                        <div className="slds-box">
                            <div className="slds-notify slds-notify_alert slds-alert_error" role="alert">
                                <span className="slds-assistive-text">error-{activeTaskProgress}</span>
                                <span className="slds-icon_container slds-icon-utility-error slds-m-right_x-small" title="deploying">
                                </span>
                                <h2>{t("task_error_occurred")}</h2>
                            </div>
                                {showErrorMessage()}
                        </div>
                    </div>
                </>
            :
            showCompleteReport()
        }

        {!serverErrorMsg && activeTask && shouldOpenActiveTaskPopUp ?
            <>
                <div className="slds-var-m-bottom_small">
                    <div className="slds-notify slds-notify_alert slds-alert_warning" role="alert">
                        <span className="slds-assistive-text">warning-{activeTaskProgress}</span>
                        <span className="slds-icon_container slds-icon-utility-warning slds-m-right_x-small" title="deploying">
                        </span>
                        <h2>{t("task_active_task", {activeTaskName:  (i18n.exists('task_' + activeTaskName) ? t('task_' + activeTaskName) : activeTaskName), moduleName: selectedModule?.moduleIdentifier})}</h2>
                    </div>
                    <CQTask variables={variables} task={activeTask} readonly={true} defaultChecked={true}></CQTask>
                </div>
            </>
            : renderQueueMessage()
        }        
            {nothingToDo ? <>
                {rerunSkippedTask === false ? 
                    <CQEmptyState lastSkipped={isSkipped} onClick={() => {
                        setRerunSkippedTask(true);
                    }}></CQEmptyState> :
                <CQSubmodules module={props.lastSkippedModule!} lastRun={true} readonlySkippedTasks={true} setViewHistoryButton={props.setViewHistoryButton} orgInfo={props.orgInfo} setCurrentModule={props.setCurrentModule} setLastRunCompletedModule={props.setLastRunCompletedModule} notifyRunStart={props.notifyRunStart}></CQSubmodules>
                }</> : 
            <>
                <div className="slds-col slds-var-p-left_small">
                    <div className="slds-text-align_right slds-var-m-bottom_small">
                        {renderOrgInfoModal()}
                        {watchPollingModal()}
                        {!isCompleted && (props.module.applicablePatches?.length || !(isUpdates)) && modulesInfo()}
                        {renderModule()}
                        {props.module.moduleIdentifier && (isUpdates || isRefresh || isFresh)?
                            <></> :(!isCompleted || hasTaskFailed)?
                            <CQTabs onSelect={(evt: any, data : any) => {
                                // The Fresh Installation tab is always appended last; skip onSelectedModule for it.
                                if (evt < upgradeTabs.length) {
                                    onSelectedModule(props?.module ? upgradeTabs[evt].label : '');
                                }
                            }}>
                                {upgradeTabs.map((cat: any) => {
                                    let upgradeModule: any ;                                    
                                    if (Object.keys(props.module).includes(cat.label)) {
                                        upgradeModule = props.module[cat.label as keyof IApplicableModules];
                                    }
                                    return <TabPanel  label={cat.label}>
                                                {hasTaskFailed && !serverErrorMsg && renderTaskError()}
                                                {!hasTaskFailed && isSuccess && renderSuccessMessage()}
                                                {!isLocked && !serverErrorMsg &&<div className= "slds-box slds-box_xx-small slds-theme_shade slds-m-bottom_small">
                                                    <CQInputs taskInputs={upgradeModule?.inputs} variables={variables} onVariableChange={(attribute, newValue) => setVariables((prev: any) => ({...prev, [attribute]: newValue}))} />
                                                    {renderInfo()}
                                                </div>}
  
                                                <Updates
                                                    activeTask={activeTask}
                                                    setSelectedTasks={setSelectedTasks}
                                                    selectedTasks={selectedTasks}
                                                    matchingTasks={matchingTasks}
                                                    module={upgradeModule?.applicableUpgrades}
                                                    setIsSuccess={setIsSuccess}
                                                    setSelectedFormodule={setSelectedFormodule}
                                                    setSelectedModuleIdentifier={setSelectedModuleIdentifier}
                                                    currentRunMap={currentRunMap}
                                                    onProceed={handleProceedButton}
                                                    runSkippedTasks={props.readonlySkippedTasks}
                                                />
                                    </TabPanel>
                                })}
                                {moduleFreshInstalls && moduleFreshInstalls.length > 0 && <TabPanel label={t("fresh_installation_tab")}>
                                    <CQFreshInstallationTab onSelectFreshPackages={props.onSelectFreshPackages} />
                                </TabPanel>}
                            </CQTabs>: <></>
                        }
                    </div>
                </div>
            </>}
    </>
}

export default CQSubmodules;